import { RESOURCE_BY_ID, RESOURCE_TYPES, type ResourceType } from "@eldrun/content";
import { deriveSeed, hash32, mix32, toUnitFloat } from "@eldrun/shared";
import { BIOME_NAMES, classifyBiome } from "./biomes.ts";
import { sampleGradient, sampleHeight, type Heightfield } from "./heightfield.ts";
import { CHUNK_SIZE, CHUNKS_PER_SIDE, WORLD_HALF } from "./params.ts";
import { samplePlanGrid, type Plan } from "./planner.ts";
import { PoiFootprints, poiOres } from "./poi-objects.ts";
import type { Poi } from "./pois.ts";

/** Chunk de uma coordenada de mundo (saturado nas bordas). */
const chunkOf = (v: number) => Math.min(CHUNKS_PER_SIDE - 1, Math.max(0, Math.floor((v + WORLD_HALF) / CHUNK_SIZE)));

/** Recursos de um chunk (ou da ilha inteira) em typed arrays transferíveis. */
export interface ResourceSet {
  count: number;
  ids: Uint32Array;
  types: Uint8Array;
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  yaw: Float32Array;
  scale: Float32Array;
}

/** id = tipo (5 bits) · célula X (12 bits) · célula Z (12 bits): estável e único na ilha. */
export function resourceId(type: number, cellX: number, cellZ: number): number {
  return ((type & 31) * 16777216 + (cellX & 4095) * 4096 + (cellZ & 4095)) >>> 0;
}

export function resourceTypeOf(id: number): number {
  return Math.floor(id / 16777216) & 31;
}

interface Candidate {
  x: number;
  z: number;
  y: number;
  yaw: number;
  scale: number;
}

/** Fração da célula usada como margem: garante distância mínima entre candidatos do mesmo tipo. */
const MARGIN = 0.18;
const TAU = 6.283185307179586;

export type ResourcePlan = Pick<Plan, "seed" | "moisture" | "coastDistance" | "landDistance"> & { pois?: readonly Poi[] };

/**
 * Gerador de recursos (doc 04, Recursos por chunk). Decisões por célula com hash sem estado de
 * (seed, tipo, célula): o resultado não depende da ordem dos chunks nem de quem gera (servidor
 * ou worker do cliente), desde que o heightfield seja o mesmo.
 */
export class ResourceGenerator {
  private readonly plan: ResourcePlan;
  private readonly heightfield: Heightfield;
  private readonly seeds: number[];
  private readonly cache = new Map<number, Candidate | null>();
  private readonly gradient = { x: 0, z: 0 };
  private readonly footprints: PoiFootprints;
  /** Veios nas âncoras das grutas, por chunk. */
  private readonly ores = new Map<number, { id: number; type: number; c: Candidate }[]>();

  constructor(plan: ResourcePlan, heightfield: Heightfield) {
    this.plan = plan;
    this.heightfield = heightfield;
    this.seeds = RESOURCE_TYPES.map((t) => deriveSeed(plan.seed, "recurso", t.index));
    this.footprints = new PoiFootprints(plan.pois ?? []);
    for (const ore of poiOres(plan.pois ?? [])) {
      const type = RESOURCE_BY_ID.get(ore.type);
      if (!type) continue;
      const key = chunkOf(ore.x) * CHUNKS_PER_SIDE + chunkOf(ore.z);
      const list = this.ores.get(key) ?? [];
      // Ids fora da grade normal das células (3900 + POI, 4000 + âncora): estáveis e únicos.
      list.push({ id: resourceId(type.index, 3900 + ore.poi, 4000 + ore.anchor), type: type.index, c: { x: ore.x, z: ore.z, y: sampleHeight(heightfield, ore.x, ore.z), yaw: ore.rot, scale: 1 } });
      this.ores.set(key, list);
    }
  }

  /** Candidato bruto de uma célula (sem exclusão entre tipos), com cache. */
  private candidate(type: ResourceType, i: number, j: number): Candidate | null {
    const key = type.index * 16777216 + i * 4096 + j;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const value = this.evaluate(type, i, j);
    this.cache.set(key, value);
    return value;
  }

  private evaluate(type: ResourceType, i: number, j: number): Candidate | null {
    const seed = this.seeds[type.index] as number;
    const u = (k: number) => toUnitFloat(hash32(seed, i, j, k));
    const u0 = u(0);
    if (u0 >= type.density) return null; // corte rápido antes de olhar o terreno
    const c = type.cell;
    const margin = c * MARGIN;
    const x = -WORLD_HALF + i * c + margin + (c - 2 * margin) * u(1);
    const z = -WORLD_HALF + j * c + margin + (c - 2 * margin) * u(2);
    // Dentro da pegada de um POI (acampamento, ruína, santuário…), nada nasce.
    if (this.footprints.inside(x, z)) return null;
    const y = sampleHeight(this.heightfield, x, z);
    const g = sampleGradient(this.heightfield, x, z, this.gradient);
    const slope = Math.sqrt(g.x * g.x + g.z * g.z);
    if (type.maxSlope !== undefined && slope > type.maxSlope) return null;
    if (type.minSlope !== undefined && slope < type.minSlope) return null;
    if (type.waterDepth) {
      const depth = -y;
      if (depth < type.waterDepth[0] || depth > type.waterDepth[1]) return null;
    } else {
      if (y < (type.minY ?? 0.3)) return null;
      if (type.maxY !== undefined && y > type.maxY) return null;
    }
    const biome = classifyBiome({
      y,
      moisture: samplePlanGrid(this.plan.moisture, x, z),
      slope,
      coastDistance: samplePlanGrid(this.plan.coastDistance, x, z),
      landDistance: samplePlanGrid(this.plan.landDistance, x, z),
    });
    const weight = type.biomes[BIOME_NAMES[biome] as keyof typeof type.biomes] ?? 0;
    if (u0 >= type.density * weight) return null;
    return {
      x,
      z,
      y,
      yaw: u(3) * TAU,
      scale: type.scale[0] + (type.scale[1] - type.scale[0]) * u(4),
    };
  }

  /** Algum tipo mais importante tem candidato perto demais de (x, z)? */
  private excluded(type: ResourceType, x: number, z: number): boolean {
    for (const other of RESOURCE_TYPES) {
      const outranks = other.priority < type.priority || (other.priority === type.priority && other.index < type.index);
      if (!outranks) continue;
      const radius = Math.max(other.exclusion, type.exclusion);
      const c = other.cell;
      const i0 = Math.floor((x - radius + WORLD_HALF) / c);
      const i1 = Math.floor((x + radius + WORLD_HALF) / c);
      const j0 = Math.floor((z - radius + WORLD_HALF) / c);
      const j1 = Math.floor((z + radius + WORLD_HALF) / c);
      for (let i = i0; i <= i1; i++) {
        for (let j = j0; j <= j1; j++) {
          const cand = this.candidate(other, i, j);
          if (!cand) continue;
          const dx = cand.x - x;
          const dz = cand.z - z;
          if (dx * dx + dz * dz < radius * radius) return true;
        }
      }
    }
    return false;
  }

  /** Recursos cujo ponto cai no chunk (cx, cz). As células dividem o chunk: nenhuma o cruza. */
  chunk(cx: number, cz: number): ResourceSet {
    const out: { id: number; type: number; c: Candidate }[] = [];
    for (const type of RESOURCE_TYPES) {
      const perChunk = CHUNK_SIZE / type.cell;
      for (let dj = 0; dj < perChunk; dj++) {
        for (let di = 0; di < perChunk; di++) {
          const i = cx * perChunk + di;
          const j = cz * perChunk + dj;
          const cand = this.candidate(type, i, j);
          if (!cand || this.excluded(type, cand.x, cand.z)) continue;
          out.push({ id: resourceId(type.index, i, j), type: type.index, c: cand });
        }
      }
    }
    out.push(...(this.ores.get(cx * CHUNKS_PER_SIDE + cz) ?? []));
    const set = emptySet(out.length);
    out.forEach((r, k) => {
      set.ids[k] = r.id;
      set.types[k] = r.type;
      set.x[k] = r.c.x;
      set.y[k] = r.c.y;
      set.z[k] = r.c.z;
      set.yaw[k] = r.c.yaw;
      set.scale[k] = r.c.scale;
    });
    return set;
  }

  /** Todos os recursos da ilha (servidor). */
  all(): ResourceSet {
    const parts: ResourceSet[] = [];
    for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) parts.push(this.chunk(cx, cz));
    return concatResources(parts);
  }
}

export function emptySet(count: number): ResourceSet {
  return {
    count,
    ids: new Uint32Array(count),
    types: new Uint8Array(count),
    x: new Float32Array(count),
    y: new Float32Array(count),
    z: new Float32Array(count),
    yaw: new Float32Array(count),
    scale: new Float32Array(count),
  };
}

export function concatResources(parts: readonly ResourceSet[]): ResourceSet {
  const total = parts.reduce((n, p) => n + p.count, 0);
  const out = emptySet(total);
  let offset = 0;
  for (const p of parts) {
    out.ids.set(p.ids, offset);
    out.types.set(p.types, offset);
    out.x.set(p.x, offset);
    out.y.set(p.y, offset);
    out.z.set(p.z, offset);
    out.yaw.set(p.yaw, offset);
    out.scale.set(p.scale, offset);
    offset += p.count;
  }
  return out;
}

export function resourceTransferables(set: ResourceSet): ArrayBuffer[] {
  return [set.ids, set.types, set.x, set.y, set.z, set.yaw, set.scale].map((a) => a.buffer as ArrayBuffer);
}

/**
 * Hash do conjunto, independente da ordem dos chunks (ordena por id). O cliente envia junto com
 * o worldHash ao terminar de carregar; o servidor recusa se divergir.
 */
export function resourcesHash(set: ResourceSet): number {
  const order = Array.from({ length: set.count }, (_, k) => k).sort((a, b) => (set.ids[a] as number) - (set.ids[b] as number));
  const f = new Float32Array(1);
  const bits = new Uint32Array(f.buffer);
  const word = (v: number) => {
    f[0] = v;
    return bits[0] as number;
  };
  let h = mix32(set.count);
  for (const k of order) {
    h = hash32(h, set.ids[k] as number, word(set.x[k] as number), word(set.y[k] as number), word(set.z[k] as number), word(set.scale[k] as number));
  }
  return h;
}
